import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Observable, Subject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';

export interface BudgetPrevisionInfoDialogData {
    /** Período salvo na simulação/assembleia (fallback quando ainda não há metadata) */
    start: string | Date;
    end: string | Date;
    metadata$: Observable<any>;
}

interface InfoMonth {
    key: string;
    label: string;
    moved: boolean;
    failed: boolean;
}

interface InfoSegment {
    sourceLabel: string;
    start: string;
    end: string;
    months: number;
    monthsWithMovement: number | null;
    /** Contagem indisponível: monthsWithMovement foi estimado pelo jobs */
    estimated: boolean;
    monthList: InfoMonth[];
}

const MONTH_LABELS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

@Component({
    selector: 'budget-prevision-info-dialog',
    templateUrl: './budget-prevision-info-dialog.component.html',
    styleUrls: ['./budget-prevision-info-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class BudgetPrevisionInfoDialogComponent implements OnInit, OnDestroy {
    loading = true;
    error = false;
    metadata: any = null;
    segments: InfoSegment[] = [];

    private readonly _destroy$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: BudgetPrevisionInfoDialogData,
        public matDialogRef: MatDialogRef<BudgetPrevisionInfoDialogComponent>,
        private _changeDetectorRef: ChangeDetectorRef,
    ) {
    }

    ngOnInit(): void {
        this.data.metadata$
            .pipe(
                takeUntil(this._destroy$),
                finalize(() => {
                    this.loading = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe(
                (metadata) => {
                    this.metadata = metadata?.details ? metadata : null;
                    this.segments = (this.metadata?.details?.segments ?? []).map(segment => this.buildSegment(segment));
                },
                () => this.error = true
            );
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    get details(): any {
        return this.metadata?.details ?? {};
    }

    get periodStart(): string | Date {
        return this.details.period?.start ?? this.data.start;
    }

    get periodEnd(): string | Date {
        return this.details.period?.end ?? this.data.end;
    }

    get sourceLabel(): string {
        const entry = this.formatDate(this.details.live_entry_date);

        switch (this.details.reason) {
            case 'not_in_live':
                return 'Base antiga — condomínio não está na Live';
            case 'no_live_entry_date':
                return 'Base antiga — condomínio na Live sem data de entrada cadastrada';
            case 'entry_after_period':
                return `Base antiga — entrada na Live (${entry}) depois do período`;
            case 'entry_before_period':
                return `Live — entrada na Live em ${entry}`;
            case 'entry_mid_period':
                return `Base antiga + Live — entrada na Live em ${entry}, no meio do período`;
            default:
                return '—';
        }
    }

    get divisors(): any {
        return this.details.divisors ?? {};
    }

    get failedMonths(): string[] {
        return this.segments
            .reduce((acc, segment) => acc.concat(segment.monthList.filter(month => month.failed)), [] as InfoMonth[])
            .map(month => month.label);
    }

    formatDate(date: string | Date): string {
        if (!date) {
            return '—';
        }

        const iso = date instanceof Date ? date.toISOString() : String(date);
        const [year, month, day] = iso.substring(0, 10).split('-');
        return `${day}/${month}/${year.substring(2)}`;
    }

    private buildSegment(segment: any): InfoSegment {
        const movementMonths: string[] | null = Array.isArray(segment?.movement_months) ? segment.movement_months : null;
        const failedMonths: string[] = Array.isArray(segment?.failed_months) ? segment.failed_months : [];
        const monthList: InfoMonth[] = [];

        if (segment?.start && segment?.end) {
            let [year, month] = String(segment.start).substring(0, 7).split('-').map(Number);
            const [endYear, endMonth] = String(segment.end).substring(0, 7).split('-').map(Number);

            while (year < endYear || (year === endYear && month <= endMonth)) {
                const key = `${year}-${String(month).padStart(2, '0')}`;
                monthList.push({
                    key,
                    label: `${MONTH_LABELS[month - 1]}/${String(year).substring(2)}`,
                    moved: movementMonths ? movementMonths.includes(key) : false,
                    failed: failedMonths.includes(key),
                });

                month++;
                if (month > 12) {
                    month = 1;
                    year++;
                }
            }
        }

        return {
            sourceLabel: segment?.source === 'live' ? 'Live' : 'Base antiga',
            start: segment?.start,
            end: segment?.end,
            months: segment?.months ?? monthList.length,
            monthsWithMovement: movementMonths ? movementMonths.length : (segment?.months_with_movement ?? null),
            estimated: !!segment?.estimated,
            // Sem a lista de meses (API da base antiga desatualizada) não dá para marcar mês a mês
            monthList: movementMonths ? monthList : [],
        };
    }
}
